import { Formik } from "formik";
import _ from "lodash";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import { ALL_AREA_CODES } from "@stw/states";
import { tenDlcRegistrationSchema } from "@stw/yup-validators";

import {
  GetRegistrationQuery,
  TenDlcExpectedTexts,
  TenDlcOrgType,
  Update10DlcBrandAndCampaignRegistrationMutationVariables,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { submitErrorField } from "~frontend/components/forms";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

import useOptOutPhraseList from "../../campaignEditor/Script/useOptOutPhraseList";
import { InternalRegistrationForm } from "./InternalRegistrationForm";

const ALLOWED_CONSECUTIVE_FAILURES = 5;

const complianceRegistrationInitialFormValues = (
  registration: GetRegistrationQuery["getRegistration"]
) => {
  const brand =
    registration.brandRegistration?.submittedAt &&
    registration.brandRegistration?.originalValues
      ? registration.brandRegistration.originalValues
      : registration.brandRegistration;

  const campaignRecord = registration;

  const campaign =
    campaignRecord?.submittedAt && campaignRecord.originalValues
      ? campaignRecord.originalValues
      : campaignRecord;
  return {
    // brand fields
    organizationName: brand.organizationName || "",
    displayName: brand.displayName || "",
    orgType: brand.orgType || "",
    ein: brand.ein || "",
    einIssuingCountry: brand.einIssuingCountry || "US",
    cvToken: brand.cvToken || "",
    focusArea: brand.focusArea || "",
    expectedTexts: brand.expectedTexts || "",
    desiredAreaCodes:
      brand.desiredAreaCodes?.filter(ac => ALL_AREA_CODES.has(ac)) ||
      ([] as string[]),
    preferredAreaCode: brand.preferredAreaCode || "",
    addressOne: brand.addressOne || "",
    addressTwo: brand.addressTwo || "",
    city: brand.city || "",
    state: brand.state || "",
    postalCode: brand.postalCode || "",
    country: brand.country || "US",
    primaryContactName: brand.primaryContactName || "",
    primaryContactPhone: brand.primaryContactPhone || "",
    primaryContactEmail: brand.primaryContactEmail || "",
    organizationContactName: brand.organizationContactName || "",
    organizationContactPhone: brand.organizationContactPhone || "",
    organizationContactEmail: brand.organizationContactEmail || "",
    orgContactIsPrimaryContact: !brand.primaryContactName,
    website: brand.website || "",

    // campaign fields
    campaignRegistrationId: registration.id || "",
    useCase: campaign?.useCase || "",
    description: campaign?.description || "",
    embeddedPhone: campaign?.embeddedPhone || false,
    optoutMessage: campaign?.optoutMessage || "",
    helpMessage: campaign?.helpMessage || "",
    termsAndConditionsLink: campaign?.termsAndConditionsLink || "",
    optInConfirmationMessage: campaign?.optInConfirmationMessage || "",
    acknowledgedCompliance: campaign?.acknowledgedCompliance || false,
    createComplianceBundle:
      campaign?.createComplianceBundle || (true as boolean),
    optInLink: campaign?.optInLink || "",
    privacyPolicyLink: campaign?.privacyPolicyLink || "",
    usageTypes: campaign?.usageTypes || [],
    sampleScript1: campaign?.sampleScripts?.[0] || "",
    sampleScript2: campaign?.sampleScripts?.[1] || "",
    sampleScript3: campaign?.sampleScripts?.[2] || "",
    sampleScript4: campaign?.sampleScripts?.[3] || "",
    additionalContext: campaign?.additionalContext || "",
    usageStartDate: campaign?.usageStartDate
      ? DateTime.fromMillis(campaign?.usageStartDate)
      : null,

    // submission
    submitToStaff: false,

    [submitErrorField]: null,
  };
};

export const InternalRegistration = ({
  registration,
  handleSave,
  handleSubmitToStaff,
  disableAutosave = false,
}: {
  registration: GetRegistrationQuery["getRegistration"];
  handleSave: (
    input: Omit<
      Update10DlcBrandAndCampaignRegistrationMutationVariables["input"],
      "subaccountId"
    >
  ) => Promise<{ errors: Error[] | string[] | undefined | null }>;
  handleSubmitToStaff?: () => Promise<{
    errors: Error[] | string[] | undefined | null;
  }>;
  disableAutosave?: boolean;
}): ReactElement => {
  const snackbar = useSnackbar();
  const subaccountContext = useSubaccount();

  const navigate = useNavigate();

  const [saveError, setSaveError] = useState<string | null>(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);

  const initialValues = complianceRegistrationInitialFormValues(registration);

  const isBrandReadonly = !!registration.brandRegistration.submittedAt;
  const isCampaignReadonly = !!registration.submittedAt;

  const { phraseList: optOutPhrases, loading: optOutPhrasesLoading } =
    useOptOutPhraseList();

  if (optOutPhrasesLoading) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={tenDlcRegistrationSchema(yup, optOutPhrases)}
        onSubmit={async (
          {
            orgContactIsPrimaryContact,
            submitToStaff,
            _submitError,
            sampleScript1,
            sampleScript2,
            sampleScript3,
            sampleScript4,
            ...values
          },
          { resetForm, setFieldValue }
        ) => {
          if (isBrandReadonly && isCampaignReadonly) {
            return;
          }
          // optimistic update
          setSaveError(null);
          resetForm({
            values: {
              ...values,
              [submitErrorField]: null,
              orgContactIsPrimaryContact,
              submitToStaff,
              sampleScript1,
              sampleScript2,
              sampleScript3,
              sampleScript4,
            },
          });
          const input = {
            ...values,
            website: values.website?.startsWith("http")
              ? values.website
              : `https://${values.website}`,
            campaignRegistrationId: registration.id,
            orgType: values.orgType
              ? (values.orgType as TenDlcOrgType)
              : undefined,
            cvToken:
              values.orgType === TenDlcOrgType.Political ? values.cvToken : "",
            focusArea:
              values.orgType === TenDlcOrgType.Private ? values.focusArea : "",
            expectedTexts: values.expectedTexts
              ? (values.expectedTexts as TenDlcExpectedTexts)
              : undefined,
            ...(orgContactIsPrimaryContact
              ? {
                  primaryContactName: "",
                  primaryContactPhone: "",
                  primaryContactEmail: "",
                }
              : {}),
            sampleScripts: [
              sampleScript1,
              sampleScript2,
              sampleScript3,
              sampleScript4,
            ],
            subaccountId: subaccountContext?.subaccount.id,
          };

          const { errors } = await handleSave(input);

          if (errors) {
            // set the error field value to also ensure the route leaving guard kicks in because the field is "dirty"
            await setFieldValue(submitErrorField, errors[0]);
            // don't automatically submit to staff on the next successful autosave
            await setFieldValue("submitToStaff", false);
            setConsecutiveFailures(prev => prev + 1);
            setSaveError(
              "Error saving registration; check your internet connection and try again."
            );
            throw errors;
          }

          setConsecutiveFailures(0);
          setSaveError(null);

          if (submitToStaff && handleSubmitToStaff) {
            const { errors: submitErrors } = await handleSubmitToStaff();

            if (submitErrors) {
              await setFieldValue("submitToStaff", false);
              throw submitErrors;
            }

            snackbar.enqueueSnackbar("Registration submitted for review", {
              variant: "success",
            });

            if (subaccountContext) {
              await navigate(
                subaccountContext.subaccountLink(`/phone-numbers`)
              );
            }
          } else {
            snackbar.enqueueSnackbar("Registration saved", {
              variant: "success",
            });
          }
        }}
      >
        <InternalRegistrationForm
          registration={registration}
          saveError={saveError}
          pauseSaving={
            disableAutosave ||
            consecutiveFailures >= ALLOWED_CONSECUTIVE_FAILURES
          }
        />
      </Formik>
    </PageContainer>
  );
};
